/**
 * Cards and accounts, and the question this page exists to answer: which charges still have no
 * receipt?
 *
 * The tally per account leads, the statement itself follows. A charge is settled by attaching a
 * document or by declaring that none is needed (bank fees, interest) - both are one click.
 */
import { ClipboardCheck, Download, Link2, Plus, Settings2, Undo2, Upload, X } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { toast } from "sonner";
import { BelegNumber } from "@/components/documents/document-badges";
import { EmptyState } from "@/components/shared/empty-state";
import { AccountSettingsDialog } from "@/components/banking/account-settings-dialog";
import { CashBook } from "@/components/banking/cash-book";
import { MatchDialog } from "@/components/banking/match-dialog";
import { StatementImportDialog } from "@/components/banking/statement-import-dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  useAccounts,
  useAutoBook,
  useAutoLink,
  useBookingRules,
  useCreateAccount,
  useImports,
  useResolveDuplicate,
  useTransactions,
  useUndoImport,
  useUnlinkDocument,
} from "@/hooks/use-banking";
import { useAllDocuments } from "@/hooks/use-documents";
import {
  ACCOUNT_DEFAULTS,
  receiptGaps,
  type BankAccount,
  type Transaction,
} from "@/lib/api/banking";
import { buildCsv, downloadCsv, MISSING_COLUMNS, missingReceiptRows } from "@/lib/export/tables";
import { formatDate, formatMoney } from "@/lib/utils/format";
import { cn } from "@/lib/utils/cn";
import { useWorkspace } from "@/lib/session/workspace-context";
import { ReadOnlyNotice } from "@/components/shared/read-only-notice";

export function BankingPage() {
  const { t } = useTranslation();
  const { workspace, canWrite } = useWorkspace();
  const [statusFilter, setStatusFilter] = useState<
    "all" | "unmatched" | "matched" | "ignored" | "review"
  >("unmatched");
  const [accountFilter, setAccountFilter] = useState("");
  const [importOpen, setImportOpen] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);
  const [matching, setMatching] = useState<Transaction | null>(null);
  const [editing, setEditing] = useState<BankAccount | null>(null);

  const { data: accounts, isPending } = useAccounts(workspace?.id);
  const { data: transactions } = useTransactions(workspace?.id, {
    status: statusFilter,
    account: accountFilter || undefined,
  });
  const { data: allTransactions } = useTransactions(workspace?.id);
  const { data: documents } = useAllDocuments(workspace?.id);
  const { data: imports } = useImports(workspace?.id);
  const unlink = useUnlinkDocument();
  const resolve = useResolveDuplicate();
  const undo = useUndoImport();
  const createAccount = useCreateAccount(workspace?.id ?? "");
  const [draft, setDraft] = useState(ACCOUNT_DEFAULTS);

  useAutoLink(allTransactions, documents, canWrite, (count) =>
    toast.success(t("close.autoLinked", { count })),
  );
  // Rows that come back (the account fee, the SVS) are booked the way a person asked for.
  const { data: rules } = useBookingRules(workspace?.id);
  useAutoBook(allTransactions, rules, canWrite, (count) =>
    toast.success(t("booking.autoBooked", { count })),
  );

  const gaps = receiptGaps(accounts ?? [], allTransactions ?? []);
  const selected = accounts?.find((account) => account.id === accountFilter);
  // Only documents nobody has claimed yet are worth offering as a match.
  const claimed = new Set(
    (allTransactions ?? []).map((transaction) => transaction.document).filter(Boolean),
  );
  const candidates = (documents ?? []).filter(
    (document) => !claimed.has(document.id) && document.status !== "archived",
  );

  function exportMissing() {
    const header = MISSING_COLUMNS.map((column) => t(`export.missing.${column}`));
    downloadCsv(
      `${workspace?.name ?? "export"}-fehlende-belege.csv`,
      buildCsv(header, missingReceiptRows(gaps)),
    );
  }

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">{t("banking.title")}</h1>
          <p className="text-sm text-muted-foreground">{t("banking.subtitle")}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button asChild variant="outline" data-testid="close-period-link">
            <Link to="/close">
              <ClipboardCheck /> {t("banking.closePeriod")}
            </Link>
          </Button>
          {canWrite && (
            <>
              <Button
                variant="outline"
                onClick={() => setAccountOpen(true)}
                data-testid="new-account"
              >
                <Plus /> {t("banking.addAccount")}
              </Button>
              <Button
                onClick={() => setImportOpen(true)}
                disabled={!accounts?.length}
                data-testid="import-statement"
              >
                <Upload /> {t("banking.import")}
              </Button>
            </>
          )}
        </div>
      </div>

      <ReadOnlyNotice />
      {isPending && <Skeleton className="h-40 w-full" aria-busy="true" />}

      {accounts && accounts.length === 0 && (
        <EmptyState
          title={t("banking.noAccountsTitle")}
          description={canWrite ? t("banking.noAccountsText") : t("banking.noAccountsReadOnly")}
          testId="no-accounts"
          action={
            canWrite && (
              <Button onClick={() => setAccountOpen(true)}>
                <Plus /> {t("banking.addAccount")}
              </Button>
            )
          }
        />
      )}

      {gaps.length > 0 && (
        <section className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {gaps.map((gap) => {
            // Before the first import "0 missing" would claim every charge has its receipt.
            const empty = !(allTransactions ?? []).some((row) => row.account === gap.account.id);
            return (
              <button
                key={gap.account.id}
                type="button"
                onClick={() => {
                  setAccountFilter(gap.account.id);
                  setStatusFilter("unmatched");
                }}
                data-testid="account-card"
                className={cn(
                  "rounded-lg border p-4 text-left transition-colors hover:bg-accent/40",
                  accountFilter === gap.account.id && "border-foreground/30 bg-accent/30",
                )}
              >
                <p className="text-sm font-medium">{gap.account.label}</p>
                <p className="ledger-id text-muted-foreground">
                  {gap.account.last4
                    ? `···· ${gap.account.last4}`
                    : gap.account.iban || t(`accountType.${gap.account.type}`)}
                </p>
                <p
                  data-testid="missing-count"
                  className={cn(
                    "mt-3 text-3xl font-semibold num",
                    empty
                      ? "text-muted-foreground"
                      : gap.missingCount > 0
                        ? "text-warning"
                        : "text-success",
                  )}
                >
                  {empty ? "–" : gap.missingCount}
                </p>
                <p className="text-xs text-muted-foreground">
                  {empty
                    ? t("banking.noRowsYet")
                    : gap.missingCount > 0
                      ? t("banking.missingSummary", { total: formatMoney(gap.missingTotal) })
                      : t("banking.allMatched")}
                </p>
                {gap.reviewCount > 0 && (
                  <p className="mt-1 text-xs font-medium text-warning" data-testid="review-count">
                    {t("banking.toReview", { count: gap.reviewCount })}
                  </p>
                )}
                <div className="mt-3 border-t pt-2 text-xs text-muted-foreground">
                  <p className="flex justify-between gap-2">
                    <span>{t("banking.balance")}</span>
                    <span className="font-medium text-foreground num" data-testid="account-balance">
                      {formatMoney(gap.balance, gap.account.currency || "EUR")}
                    </span>
                  </p>
                  {gap.statementDifference !== null && (
                    <p
                      data-testid="statement-check"
                      className={cn(
                        "mt-1",
                        gap.statementDifference === 0 ? "text-success" : "text-warning",
                      )}
                    >
                      {gap.statementDifference === 0
                        ? t("banking.statementAgrees")
                        : t("banking.statementDiffers", {
                            amount: formatMoney(gap.statementDifference),
                          })}
                    </p>
                  )}
                  {gap.unexplainedCount > 0 && (
                    <p className="mt-1" data-testid="unexplained-count">
                      {t("banking.unexplained", { count: gap.unexplainedCount })}
                    </p>
                  )}
                </div>
              </button>
            );
          })}
        </section>
      )}

      <section className="space-y-3">
        <div className="flex flex-wrap items-end gap-3">
          <div className="space-y-1">
            <Label className="text-xs text-muted-foreground" htmlFor="tx-status">
              {t("banking.filterStatus")}
            </Label>
            <Select value={statusFilter} onValueChange={(value) => setStatusFilter(value as never)}>
              <SelectTrigger id="tx-status" className="w-48" data-testid="filter-tx-status">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="unmatched">{t("transactionStatus.unmatched")}</SelectItem>
                <SelectItem value="matched">{t("transactionStatus.matched")}</SelectItem>
                <SelectItem value="ignored">{t("transactionStatus.ignored")}</SelectItem>
                <SelectItem value="review">{t("transactionStatus.review")}</SelectItem>
                <SelectItem value="all">{t("common.all")}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {accountFilter && (
            <Button variant="ghost" size="sm" onClick={() => setAccountFilter("")}>
              <X /> {t("banking.clearAccount")}
            </Button>
          )}
          {selected && canWrite && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setEditing(selected)}
              data-testid="account-settings"
            >
              <Settings2 /> {t("accountBooks.open")}
            </Button>
          )}
          <Button
            variant="outline"
            size="sm"
            className="ml-auto"
            onClick={exportMissing}
            disabled={gaps.every((gap) => gap.missingCount === 0)}
            data-testid="export-missing"
          >
            <Download /> {t("banking.exportMissing")}
          </Button>
        </div>

        {transactions && transactions.length === 0 && (
          <EmptyState
            title={
              statusFilter === "unmatched" && allTransactions?.length
                ? t("banking.nothingMissing")
                : t("banking.noTransactions")
            }
            description={
              statusFilter === "unmatched" && allTransactions?.length
                ? t("banking.nothingMissingHint")
                : canWrite
                  ? t("banking.importHint")
                  : t("banking.importHintReadOnly")
            }
            testId="no-transactions"
          />
        )}

        {transactions && transactions.length > 0 && (
          <div className="overflow-hidden rounded-lg border">
            <Table data-testid="transactions-table">
              {/* On a phone each row is a small grid - counterparty and amount, then date and
                  receipt, the action beside them - and the header names nothing anyone reads. */}
              <TableHeader className="hidden sm:table-header-group">
                <TableRow>
                  <TableHead>{t("banking.date")}</TableHead>
                  <TableHead>{t("banking.counterparty")}</TableHead>
                  <TableHead className="text-right">{t("banking.amount")}</TableHead>
                  <TableHead>{t("banking.receipt")}</TableHead>
                  <TableHead className="w-40" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {transactions.map((transaction) => (
                  <TableRow
                    key={transaction.id}
                    className="grid grid-cols-[minmax(0,1fr)_auto] gap-x-3 gap-y-1 p-3 sm:table-row sm:p-0"
                    data-testid="transaction-row"
                  >
                    <TableCell className="col-start-1 row-start-2 p-0 text-xs text-muted-foreground num sm:p-2 sm:text-sm">
                      {formatDate(transaction.bookingDate)}
                    </TableCell>
                    <TableCell className="col-start-1 row-start-1 p-0 sm:max-w-72 sm:p-2">
                      <p className="truncate font-medium">{transaction.counterparty || "—"}</p>
                      <p className="truncate text-xs text-muted-foreground">
                        {transaction.description}
                      </p>
                    </TableCell>
                    <TableCell
                      className={cn(
                        "col-start-2 row-start-1 p-0 text-right font-medium num sm:p-2",
                        transaction.amount > 0 && "text-success",
                      )}
                    >
                      {formatMoney(transaction.amount, transaction.currency)}
                    </TableCell>
                    <TableCell className="col-start-1 row-start-3 p-0 sm:p-2">
                      {transaction.status === "matched" && (
                        <span className="flex flex-wrap items-center gap-2">
                          <BelegNumber value={transaction.expand?.document?.docNumber} />
                          {transaction.matchedBy === "auto" && (
                            <span className="text-xs text-matched" data-testid="auto-matched">
                              {t("banking.autoMatched")}
                            </span>
                          )}
                        </span>
                      )}
                      {transaction.status === "ignored" && (
                        <span className="text-xs text-muted-foreground" data-testid="booked-as">
                          {transaction.bookingType
                            ? [
                                t(`booking.types.${transaction.bookingType}`),
                                transaction.bookingNote,
                                transaction.matchedBy === "auto" && t("booking.byRule"),
                              ]
                                .filter(Boolean)
                                .join(" · ")
                            : t("banking.noReceiptNeeded")}
                        </span>
                      )}
                      {/* Money in (a refund) is no missing receipt: not counted, so not "missing" (QA-0052). */}
                      {transaction.status === "unmatched" &&
                        !transaction.possibleDuplicateOf &&
                        (transaction.amount < 0 ? (
                          <span className="text-xs text-warning">{t("banking.missing")}</span>
                        ) : (
                          <span className="text-xs text-muted-foreground" data-testid="money-in">
                            {t("banking.moneyIn")}
                          </span>
                        ))}
                      {transaction.possibleDuplicateOf && (
                        <span
                          className="text-xs font-medium text-warning"
                          data-testid="possible-duplicate"
                          title={
                            transaction.expand?.possibleDuplicateOf
                              ? `${formatDate(transaction.expand.possibleDuplicateOf.bookingDate)} · ${transaction.expand.possibleDuplicateOf.counterparty} · ${formatMoney(transaction.expand.possibleDuplicateOf.amount, transaction.currency)}`
                              : undefined
                          }
                        >
                          {t("banking.possibleDuplicate")}
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="col-start-2 row-span-2 row-start-2 self-end p-0 text-right sm:p-2">
                      {canWrite && transaction.possibleDuplicateOf && (
                        <div className="flex flex-col items-end gap-1 sm:flex-row sm:justify-end">
                          <Button
                            size="sm"
                            variant="outline"
                            data-testid="merge-duplicate"
                            disabled={resolve.isPending}
                            onClick={async () => {
                              await resolve.mutateAsync({ id: transaction.id, decision: "merge" });
                              toast.success(t("banking.duplicateMerged"));
                            }}
                          >
                            {t("banking.sameCharge")}
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            data-testid="keep-duplicate"
                            disabled={resolve.isPending}
                            onClick={async () => {
                              await resolve.mutateAsync({ id: transaction.id, decision: "keep" });
                              toast.success(t("banking.duplicateKept"));
                            }}
                          >
                            {t("banking.differentCharge")}
                          </Button>
                        </div>
                      )}
                      {canWrite &&
                        transaction.status === "unmatched" &&
                        !transaction.possibleDuplicateOf && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setMatching(transaction)}
                            data-testid="match-transaction"
                          >
                            <Link2 /> {t("banking.attach")}
                          </Button>
                        )}
                      {canWrite && transaction.status !== "unmatched" && (
                        <Button
                          size="sm"
                          variant="ghost"
                          data-testid="unlink-transaction"
                          onClick={async () => {
                            await unlink.mutateAsync(transaction.id);
                            toast.success(t("banking.unlinked"));
                          }}
                        >
                          {t("banking.undo")}
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </section>

      {selected?.type === "cash" && <CashBook account={selected} canWrite={canWrite} />}

      {imports && imports.length > 0 && (
        <section className="space-y-2">
          <h2 className="font-medium">{t("banking.importsTitle")}</h2>
          <ul className="divide-y rounded-lg border bg-card" data-testid="imports">
            {imports.slice(0, 10).map((record) => (
              <li
                key={record.id}
                className="flex flex-wrap items-center gap-x-4 gap-y-1 px-4 py-2.5 text-sm"
                data-testid="import-row"
              >
                <span className="max-w-72 truncate font-medium">{record.fileName}</span>
                <span className="ledger-id text-muted-foreground">
                  {record.format.toUpperCase()}
                </span>
                <span className="text-muted-foreground">
                  {accounts?.find((entry) => entry.id === record.account)?.label}
                </span>
                <span className="text-xs text-muted-foreground">
                  {t("banking.importSummary", {
                    created: record.createdCount + record.reviewCount,
                    merged: record.mergedCount,
                    known: record.knownCount,
                  })}
                </span>
                <span className="ml-auto text-xs text-muted-foreground">
                  {formatDate(record.created)}
                </span>
                {canWrite && (
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button
                        size="sm"
                        variant="ghost"
                        data-testid="undo-import"
                        disabled={undo.isPending}
                      >
                        <Undo2 /> {t("banking.undoImport")}
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>{t("banking.undoImportTitle")}</AlertDialogTitle>
                        <AlertDialogDescription>
                          {t("banking.undoImportText")}
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
                        <AlertDialogAction
                          data-testid="undo-import-confirm"
                          onClick={async () => {
                            const result = await undo.mutateAsync(record);
                            toast.success(
                              t("banking.importUndone", {
                                deleted: result.deleted,
                                kept: result.kept,
                              }),
                            );
                          }}
                        >
                          {t("banking.undoImport")}
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}

      <AccountSettingsDialog account={editing} onOpenChange={(open) => !open && setEditing(null)} />

      <MatchDialog
        transaction={matching}
        documents={candidates}
        onOpenChange={(open) => !open && setMatching(null)}
      />

      {accounts && (
        <StatementImportDialog
          open={importOpen}
          onOpenChange={setImportOpen}
          accounts={accounts}
          defaultAccountId={accountFilter || accounts[0]?.id}
        />
      )}

      <Dialog open={accountOpen} onOpenChange={setAccountOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("banking.addAccount")}</DialogTitle>
            <DialogDescription>{t("banking.addAccountHint")}</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="account-label">{t("banking.accountLabel")}</Label>
              <Input
                id="account-label"
                value={draft.label}
                data-testid="account-label"
                onChange={(event) => setDraft({ ...draft, label: event.target.value })}
              />
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="account-type">{t("banking.accountType")}</Label>
                <Select
                  value={draft.type}
                  onValueChange={(value) => setDraft({ ...draft, type: value as never })}
                >
                  <SelectTrigger id="account-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="credit_card">{t("accountType.credit_card")}</SelectItem>
                    <SelectItem value="bank">{t("accountType.bank")}</SelectItem>
                    <SelectItem value="cash">{t("accountType.cash")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="account-last4">
                  {draft.type === "credit_card" ? t("banking.last4") : t("banking.iban")}
                </Label>
                <Input
                  id="account-last4"
                  className="num"
                  maxLength={draft.type === "credit_card" ? 4 : 40}
                  value={draft.type === "credit_card" ? draft.last4 : draft.iban}
                  onChange={(event) =>
                    setDraft(
                      draft.type === "credit_card"
                        ? { ...draft, last4: event.target.value.replace(/\D/g, "") }
                        : { ...draft, iban: event.target.value.toUpperCase() },
                    )
                  }
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="account-institution">{t("banking.institution")}</Label>
              <Input
                id="account-institution"
                value={draft.institution}
                onChange={(event) => setDraft({ ...draft, institution: event.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAccountOpen(false)}>
              {t("common.cancel")}
            </Button>
            <Button
              disabled={draft.label.trim().length === 0 || createAccount.isPending}
              data-testid="save-account"
              onClick={async () => {
                try {
                  await createAccount.mutateAsync(draft);
                  toast.success(t("banking.accountCreated"));
                  setDraft(ACCOUNT_DEFAULTS);
                  setAccountOpen(false);
                } catch (error) {
                  // The plan's limit says so in words (lib/plan/plan-errors.ts).
                  toast.error(error instanceof Error ? error.message : t("banking.accountFailed"));
                }
              }}
            >
              {t("common.save")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
